/**
 * Profiles: the five libraries a brewer reuses (PLAN-PROFILES P3).
 *
 * Equipment, Water, Mash, Fermentation and Additions on one page with the tab
 * in the URL, so a link can name one and the back button works.
 *
 * ## Why one page, and why the sidebar says "Profiles"
 *
 * Four of the sidebar's entries were already profile libraries; two more as
 * their own entries would be a navigation made of one idea repeated. And
 * "Profiles" is the word a brewer looks for even though a more accurate
 * container label would read better: "mash profile" and "water profile" are
 * what BeerSmith and Brewfather call these, and "a Burton water profile" is
 * something brewers say out loud. Recognition beats elegance in a sidebar.
 *
 * The collision that created — Settings already had a "Profile" meaning a
 * display name — was resolved by renaming that one to "Public profile" (P3b),
 * not by picking a worse word here.
 *
 * ## The tabs group by what you reuse, not by how it binds
 *
 * This is the one thing the tab strip risks lying about, so each tab says it.
 * **Equipment binds by reference**: a recipe stores the profile's id and
 * re-reads it, so editing the profile moves every recipe on it, which is right
 * for a rig that really did change. **The others copy**: applying puts the
 * values into the recipe and the link ends there, so last year's beer keeps the
 * water and the mash it was actually brewed with.
 *
 * Both are correct and they are not the same, and a brewer who assumes one when
 * the other is true gets a nasty surprise exactly once. The Equipment tab's
 * note is the important one, because it is the exception.
 */

import type { ReactNode } from 'react';
import { DocsLink } from '@/components/docs-link';
import { EquipmentPage } from '@/components/equipment/equipment-page';
import { MiscProfilesTab } from '@/components/profiles/misc-profiles-tab';
import { ScheduleProfilesTab } from '@/components/profiles/schedule-profiles-tab';
import { WaterProfilesPage } from '@/components/water/water-profiles-page';
import { cn } from '@/lib/utils';
import {
  useFermentationProfiles,
  useMashProfiles,
  useMiscProfiles,
} from '@/store/workspace-context';

/** The tabs, in the order a recipe meets them. */
export const PROFILE_TABS = [
  { id: 'equipment', label: 'Equipment' },
  { id: 'water', label: 'Water' },
  { id: 'mash', label: 'Mash' },
  { id: 'fermentation', label: 'Fermentation' },
  { id: 'additions', label: 'Additions' },
] as const;

/** One of the tab ids. */
export type ProfileTab = (typeof PROFILE_TABS)[number]['id'];

/** The tab a bad or missing URL segment falls back to. */
export const DEFAULT_PROFILE_TAB: ProfileTab = 'equipment';

/** Narrow an arbitrary URL segment to a tab, or the default. */
export function toProfileTab(value: string | undefined): ProfileTab {
  return PROFILE_TABS.some((tab) => tab.id === value) ? (value as ProfileTab) : DEFAULT_PROFILE_TAB;
}

export interface ProfilesPageProps {
  readonly tab: ProfileTab;
  readonly onTabChange: (tab: ProfileTab) => void;
}

export function ProfilesPage({ tab, onTabChange }: ProfilesPageProps) {
  const mashProfiles = useMashProfiles();
  const fermentationProfiles = useFermentationProfiles();
  const miscProfiles = useMiscProfiles();

  return (
    <div className="flex min-w-0 flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h1 className="font-semibold text-lg tracking-tight">Profiles</h1>
          <DocsLink docs="profiles" label="Profiles" />
        </div>
        <p className="max-w-prose text-muted-foreground text-sm">
          The things you set up once and reuse: your kit, your water, the schedules you brew to, and
          the additions you put in most beers.
        </p>
      </header>

      <nav
        aria-label="Profile libraries"
        className="flex gap-1 overflow-x-auto border-border border-b"
      >
        {PROFILE_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            aria-current={id === tab ? 'page' : undefined}
            onClick={() => onTabChange(id)}
            className={cn(
              '-mb-px shrink-0 border-b-2 px-3 py-2 font-medium text-sm transition-colors',
              id === tab
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* The two notes below carry a clause each that the tabs' own page
          headings used to say, before those headings went (PLAN-UX-PASS F2):
          what an equipment profile holds, and where the water work a recipe
          owns actually happens. */}
      {tab === 'equipment' && (
        <TabBody note="A recipe points at its equipment profile, so editing one here moves every recipe using it. That is the point: it describes your rig, and holds the volume losses, boil-off and efficiency for each system you brew on.">
          <EquipmentPage />
        </TabBody>
      )}
      {tab === 'water' && (
        <TabBody note="Applying a water profile copies it into the recipe, so editing one here leaves your existing recipes alone. This is where your own reports and the classic city analyses live; dilution, salts and mash pH are decisions about a beer, so you make those in the recipe's water panel.">
          <WaterProfilesPage />
        </TabBody>
      )}
      {tab === 'mash' && (
        <TabBody note="Applying a mash profile copies its steps into the recipe, so editing one here leaves your existing recipes alone. You make your own by adjusting a recipe's mash schedule and saving it from there.">
          <ScheduleProfilesTab
            profiles={mashProfiles}
            domain="mashProfile"
            noun="mash schedule"
            emptyMessage="No mash schedules yet."
          />
        </TabBody>
      )}
      {tab === 'fermentation' && (
        <TabBody note="Applying a fermentation profile copies its steps into the recipe, so editing one here leaves your existing recipes alone. You make your own by adjusting a recipe's fermentation schedule and saving it from there.">
          <ScheduleProfilesTab
            profiles={fermentationProfiles}
            domain="fermentationProfile"
            noun="fermentation schedule"
            emptyMessage="No fermentation schedules yet."
          />
        </TabBody>
      )}
      {tab === 'additions' && (
        <TabBody note="Applying an additions profile adds its rows to the recipe, scaled to the recipe's batch size, and skips anything already there. Editing one here leaves your existing recipes alone. You make your own by setting up a recipe's misc additions and saving them from there.">
          <MiscProfilesTab profiles={miscProfiles} />
        </TabBody>
      )}
    </div>
  );
}

/**
 * A tab, with the sentence that says how it binds to a recipe.
 *
 * Every tab carries one, including the ones that agree with each other. A note
 * only on the odd one out would read as a warning about equipment rather than
 * as the ordinary fact it is, and a brewer comparing two tabs would have to
 * infer the silence.
 */
function TabBody({ note, children }: { readonly note: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">{note}</p>
      {children}
    </section>
  );
}
